// Extracted from original/src/components/CashTransfers.tsx:2188-2366 — the details window of a transfer ("تفاصيل
// ومعلومات الحوالة"), frame and buttons only (ADR-08 §2.1). The voucher it shows is the A4 voucher template of
// `@faylasuf/print` (`TransferVoucherA4`, the old `receipt-payment-zone-<id>` markup), passed in as `voucher` so the
// same markup is shown on screen and printed (D-064 (2): the A4 voucher stays).
// Kept: the overlay, the motion frame, the two buttons and their classes.
// Cut: `printElement('receipt-payment-zone-…')` with the `document.title` swap (:2345-2350) → `onPrint` (the container
// records the print, D-147, and prints through `@faylasuf/print`); `selectedReceipt` → `open`.
// Added: the cancellation banner of a cancelled transfer with its reason (D-086 "stays visible as cancelled with its
// reason"; V-36), data-testid attributes.
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Download, Printer } from 'lucide-react';
import { useT } from '../i18n-context.js';

export interface TransferReceiptModalProps {
  readonly open: boolean;
  readonly voucher: ReactNode;
  /** A cancelled / rejected transfer: the reason line shown above the voucher (never printed). */
  readonly statusNote: string | null;
  readonly canPrint: boolean;
  readonly onPrint: () => void;
  readonly onClose: () => void;
  /** [C35] D-149 per-copy format and D-146 server PDF (new elements, V-96). */
  readonly format?: 'a4' | '80mm';
  readonly onFormatChange?: (f: 'a4' | '80mm') => void;
  readonly onPdf?: () => void;
}

export function TransferReceiptModal(p: TransferReceiptModalProps) {
  const t = useT();
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm" data-testid="transfer-receipt">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border border-slate-150 dark:border-[#30363d] w-full max-w-xl shadow-2xl p-6 max-h-[92vh] overflow-y-auto"
          >
            {p.statusNote !== null && (
              <div className="mb-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-xs font-black text-right" data-testid="transfer-receipt-status">
                ✖ {p.statusNote}
              </div>
            )}
            {p.voucher}

            {/* [C35] D-149 format of this copy (V-96) */}
            {p.canPrint && p.format && p.onFormatChange && (
              <div className="mt-5 flex bg-gray-100 dark:bg-[#05070a] p-1 rounded-lg border border-gray-200 dark:border-[#30363d] w-full" data-testid="voucher-format">
                {(['a4', '80mm'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => p.onFormatChange!(f)}
                    data-testid={`voucher-format-${f}`}
                    className={`flex-1 py-1.5 px-3 rounded-md text-[10px] font-black transition-all cursor-pointer ${p.format === f ? 'bg-rose-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-[#f0f6fc]'}`}
                  >
                    {t(`print.format.${f}`)}
                  </button>
                ))}
              </div>
            )}

            {/* Window buttons */}
            <div className="mt-6 flex gap-3">
              {p.canPrint && (
                <button
                  onClick={p.onPrint}
                  className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-black text-xs rounded-2xl flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
                  data-testid="transfer-receipt-print"
                >
                  <Printer size={16} />
                  {t('ct.print')}
                </button>
              )}
              {p.canPrint && p.onPdf && (
                <button
                  onClick={p.onPdf}
                  className="px-5 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs rounded-2xl flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
                  data-testid="transfer-receipt-pdf"
                >
                  <Download size={16} />
                  PDF
                </button>
              )}
              <button
                onClick={p.onClose}
                className="px-6 py-3.5 bg-gray-100 dark:bg-[#161b22] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all"
                data-testid="transfer-receipt-close"
              >
                {t('ct.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
